import React from 'react';
import {
	Grid,
	Typography,
	CircularProgress,
	IconButton as IconBtn,
	TextField,
	Box,
	List,
	ListItem,
	ListItemText,
	Button,
} from '@mui/material';
import {
	CircleClose,
	CopyIcon,
	GenerateIcon,
	UploadFile,
} from '../../assets/icons/Icons';
import IconButton from '../../components/IconButton';
import CustomAccordion from '../../components/CustomAccordion';
import HighlightWithinTextarea from 'react-highlight-within-textarea';
import { FileUploadDiv, helperText } from '../../assets/theme/Theme';
import { MAIN_THEME_COLOR } from '../../assets/theme/Colors';
import Dropzone from 'react-dropzone';
import FileContentTile from '../../components/FileContentTile';

function FileItem({ file, onRemoveFiles, isLoading }) {
	return (
		<Grid
			container
			alignItems="center"
			justifyContent="space-between"
			wrap="nowrap"
			sx={{
				border: '1px solid #D9D9D9',
				borderRadius: '8px',
				padding: '6px 10px',
				maxWidth: '220px',
				backgroundColor: '#FAFAFA',
			}}>
			<Grid
				item
				xs
				sx={{
					overflow: 'hidden',
				}}>
				<Typography fontSize="13px" noWrap title={file?.name}>
					{file?.name}
				</Typography>
			</Grid>
			<Grid item>
				<IconBtn
					disableRipple
					style={{ padding: 0, marginLeft: '6px' }}
					disabled={isLoading}
					onClick={() => onRemoveFiles(file)}>
					<CircleClose />
				</IconBtn>
			</Grid>
		</Grid>
	);
}

function AttachInstructionFile({
	loading,
	readTextFile,
	inputError,
	selectedFiles,
	onRemoveFiles,
}) {
	return (
		<Grid container alignItems="center" spacing={2} wrap="nowrap">
			<Grid item xs>
				{selectedFiles && selectedFiles.length > 0 && (
					<Grid container spacing={2}>
						{selectedFiles.map((item, index) => (
							<Grid item key={index}>
								<FileItem
									file={item}
									onRemoveFiles={onRemoveFiles}
									isLoading={loading}
								/>
							</Grid>
						))}
					</Grid>
				)}
			</Grid>
			<Grid item>
				{!loading && (
					<Dropzone
						disabled={loading}
						onDrop={acceptedFiles => readTextFile(acceptedFiles)}>
						{({ getRootProps, getInputProps }) => (
							<div
								{...getRootProps()}
								style={{
									padding: '6px 10px',
									borderRadius: '5px',
									whiteSpace: 'nowrap',
									cursor: 'pointer',
								}}>
								<input {...getInputProps()} />
								<Typography
									fontSize="14px"
									fontWeight={'600'}
									color={MAIN_THEME_COLOR}>
									+ Attach files
								</Typography>
							</div>
						)}
					</Dropzone>
				)}
			</Grid>
		</Grid>
	);
}

function GeneratedPlanTemplate({
	data,
	feedbacks,
	onPlanChange,
	onChangeFeedback,
	onAddFeedBackToPlan,
	isSubPlanLoading,
	expanded,
	handleExpansion,
	onClickFirstBtn,
	popupBtnState,
	isCurrentPlanFetched,
	file,
	onRemoveFiles,
	planType,
	feedbackError,
	onGeneratePdf,
	onShowFileUpload,
	noReviewError,
	onStoreOptimizedPlan,
	mainFeedback,
	onChangeMainFeedback,
	mainFeedbackError,
	onOptimizePlan,
	isOptimizingWholePlan,
	feedbackFiles,
	mainFeedbackFile,
	onChangeFeedbackFile,
	onChangeMainFeedbackFile,
	onRemoveMainFeedbackFiles,
	onRemoveFeedbackFiles,
}) {
	function covertArrayToBulletString(array) {
		return array.map(item => `• ${item}`).join('\n');
	}

	function checkSubPlanLoading(index, itemIndex) {
		if (
			isSubPlanLoading.loading &&
			isSubPlanLoading.planIndex === index &&
			isSubPlanLoading.subPlanIndex === itemIndex
		) {
			return true;
		} else {
			return false;
		}
	}

	function onCopyPlan(data) {
		const value = data.map(item => `• ${item}`).join('\n');

		if (navigator.clipboard) {
			navigator.clipboard.writeText(value);
		} else {
			const tempTextArea = document.createElement('textarea');
			tempTextArea.value = value;
			document.body.appendChild(tempTextArea);
			tempTextArea.select();
			document.execCommand('copy');
			document.body.removeChild(tempTextArea);
		}
	}

	const handleKeyDown = (e, valueString, index, itemIndex) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			onPlanChange(`${valueString}\n• `, index, itemIndex);
		} else if (e.key === 'Backspace') {
			if (valueString.endsWith('\n• ')) {
				e.preventDefault();
				const newString = valueString.slice(0, -3); // Remove last newline + bullet + space
				onPlanChange(newString, index, itemIndex);
			} else if (valueString.endsWith('•')) {
				e.preventDefault();
				const newString = valueString.slice(0, -1); // Remove last bullet
				onPlanChange(newString, index, itemIndex);
			}
		}
	};

	return (
		<>
			<Grid container>
				<Grid
					container
					spacing={3}
					columns={{ xs: 4, sm: 8, md: 12 }}
					justifyContent={'center'}
					alignItems={'center'}>
					{onShowFileUpload && (
						<Grid item xs={12} paddingBottom={'20px'}>
							<IconBtn
								disableRipple
								onClick={() => onClickFirstBtn('UPLOAD')}
								style={{
									backgroundColor: 'transparent',
									padding: '0px',
									width: '100%',
								}}>
								<Grid
									container
									flexDirection={'column'}
									height={'280px'}
									borderRadius={'10px'}
									border={FileUploadDiv.border}
									bgcolor={
										popupBtnState === 'Upload'
											? '#28696D'
											: '#FFFFFF'
									}
									justifyContent={'center'}
									alignItems={'center'}>
									<UploadFile />
									<Typography
										variant="h6"
										width={'50%'}
										fontWeight={'600'}
										marginTop={'15px'}
										color={
											popupBtnState === 'Upload'
												? '#FFFFFF'
												: '#28696D'
										}>
										Upload files
									</Typography>
								</Grid>
							</IconBtn>
						</Grid>
					)}
				</Grid>
			</Grid>
			{isCurrentPlanFetched && (
				<>
					{onShowFileUpload && (
						<Grid
							container
							flexDirection={'column'}
							marginTop={'15px'}>
							<Typography
								fontSize={'16px'}
								marginBottom={'5px'}
								color={'#7C7C7C'}>
								Uploaded Plan
							</Typography>
							<Grid container alignItems="center" spacing={2}>
								{file.map((item, index) => {
									return (
										<Grid
											item
											xs={6}
											sm={4}
											md={3}
											key={index}>
											<FileContentTile
												key={index}
												file={item}
												onRemoveFiles={onRemoveFiles}
											/>
										</Grid>
									);
								})}
							</Grid>
						</Grid>
					)}
					<Typography
						fontSize={'16px'}
						marginTop={'15px'}
						color={'#7C7C7C'}>
						{planType}
					</Typography>
					{data.map((value, index) => {
						return (
							<CustomAccordion
								key={index}
								id={index}
								expanded={expanded === index}
								handleExpansion={handleExpansion(index)}
								mainTitle={value.title}
								showTag={value.isPlanModified}>
								<Grid container marginTop={'10px'}>
									{value.bodyContent.map(
										(item, itemIndex) => {
											return (
												<Grid
													key={itemIndex}
													container
													paddingX={'15px'}
													flexDirection={'column'}>
													<Grid
														container
														justifyContent={
															'space-between'
														}
														alignItems={'center'}
														paddingX={'15px'}>
														<Typography
															fontSize={'14px'}
															color={'#28696D'}
															fontWeight={'600'}>
															{item.label}
														</Typography>
														<IconBtn
															disableFocusRipple
															onClick={() =>
																onCopyPlan(
																	item.content,
																)
															}
															style={{
																padding: '0px',
															}}>
															<CopyIcon />
														</IconBtn>
													</Grid>
													<Box
														margin={'15px'}
														padding={'10px'}
														marginRight={'15px'}
														sx={{
															fontSize: '14px',
															border: '1px solid transparent',
															borderRadius: '5px',
															transition:
																'border-color 0.3s ease',
															'&:hover': {
																border: '1px solid #e0dcdc',
															},
														}}>
														<HighlightWithinTextarea
															highlight={
																value.modifiedSection &&
																value
																	.modifiedSection[
																	itemIndex
																]
															}
															value={covertArrayToBulletString(
																item.content,
															)}
															onChange={value => {
																onPlanChange(
																	value,
																	index,
																	itemIndex,
																);
															}}
															keyBindingFn={e => {
																handleKeyDown(
																	e,
																	covertArrayToBulletString(
																		item.content,
																	),
																	index,
																	itemIndex,
																);
															}}
														/>
													</Box>
												</Grid>
											);
										},
									)}
								</Grid>
								{noReviewError.itemIndex === index &&
									noReviewError.status && (
										<Typography
											marginY={'5px'}
											marginLeft={'20px'}
											style={helperText}>
											{noReviewError.msg}
										</Typography>
									)}

								<Grid container padding={'15px'}>
									<Typography
										paddingBottom={'10px'}
										fontSize={'14px'}
										fontWeight={'600'}
										color={'#7C7C7C'}>
										Instructions (Optional)
									</Typography>
									<Grid
										border={
											value.enableUserPrompt ||
											(feedbackError.itemIndex ===
												index &&
												feedbackError.status)
												? '1px solid red'
												: '1px solid #D9D9D9'
										}
										bgcolor={'#FFFFFF'}
										borderRadius={'5px 0px 0px 5px'}
										marginX={'10px'}
										width={'100%'}>
										<TextField
											placeholder="Add instruction to consider in the re-generation"
											multiline
											sx={{
												'& fieldset': {
													border: 'none',
												},
												width: '100%',
												height: '100px',
												'&::placeholder': {
													fontSize: '14px',
												},
											}}
											onChange={e =>
												onChangeFeedback(
													e.target.value,
													index,
												)
											}
											value={feedbacks[index]}
											minRows={3}
											maxRows={6}
										/>
										<Grid
											container
											alignItems="center"
											justifyContent="space-between"
											spacing={1}
											paddingBottom={'5px'}
											paddingX={'5px'}
											marginTop="8px">
											<Grid item xs>
												<AttachInstructionFile
													inputError={feedbackError}
													loading={checkSubPlanLoading(
														index,
													)}
													readTextFile={e =>
														onChangeFeedbackFile(
															e,
															index,
														)
													}
													selectedFiles={
														feedbackFiles[index]
													}
													onRemoveFiles={value =>
														onRemoveFeedbackFiles(
															value,
															index,
														)
													}
												/>
											</Grid>
											<Grid item>
												<Grid
													container
													alignItems="center"
													spacing={1}>
													{checkSubPlanLoading(
														index,
													) && (
														<Grid
															paddingRight={
																'10px'
															}
															paddingTop={'10px'}>
															<CircularProgress
																sx={{
																	color: '#28696D',
																}}
																size={'2rem'}
															/>
														</Grid>
													)}
													<Grid item>
														<IconButton
															text={
																'Optimize Plan'
															}
															Icon={GenerateIcon}
															onClick={() =>
																onAddFeedBackToPlan(
																	index,
																	value.enableUserPrompt,
																)
															}
															disabled={checkSubPlanLoading(
																index,
															)}
														/>
													</Grid>
												</Grid>
											</Grid>
										</Grid>
									</Grid>
									<Grid
										container
										width={'100%'}
										justifyContent={'flex-end'}
										alignItems={'center'}
										paddingBottom={'10px'}
										paddingRight={'10px'}>
										{feedbackError.itemIndex === index &&
											feedbackError.status && (
												<Typography
													marginRight={'10px'}
													style={helperText}>
													{feedbackError.msg}
												</Typography>
											)}
									</Grid>
								</Grid>
							</CustomAccordion>
						);
					})}
					<Grid container paddingY={'15px'}>
						<Typography
							paddingBottom={'10px'}
							fontSize={'14px'}
							fontWeight={'600'}
							color={'#7C7C7C'}>
							Instructions (Optional)
						</Typography>
						<Grid
							border={
								mainFeedbackError.status
									? '1px solid red'
									: '1px solid #D9D9D9'
							}
							bgcolor={'#FFFFFF'}
							borderRadius={'5px 0px 0px 5px'}
							marginX={'10px'}
							width={'100%'}
							direction="column">
							<TextField
								placeholder="Provide instruction to optimize whole plan"
								multiline
								sx={{
									'& fieldset': {
										border: 'none',
									},
									width: '100%',
									height: '100px',
									'&::placeholder': {
										fontSize: '14px',
									},
								}}
								onChange={e =>
									onChangeMainFeedback(e.target.value)
								}
								value={mainFeedback}
								minRows={3}
								maxRows={6}
							/>
							<Grid
								container
								alignItems="center"
								justifyContent="space-between"
								spacing={1}
								paddingBottom={'5px'}
								paddingX={'5px'}
								marginTop="8px">
								<Grid item xs>
									<AttachInstructionFile
										inputError={mainFeedbackError}
										loading={isOptimizingWholePlan}
										readTextFile={onChangeMainFeedbackFile}
										selectedFiles={mainFeedbackFile}
										onRemoveFiles={
											onRemoveMainFeedbackFiles
										}
									/>
								</Grid>
								<Grid item>
									<Grid
										container
										alignItems="center"
										spacing={1}>
										<Grid item>
											<IconButton
												text="Optimize Plan"
												Icon={GenerateIcon}
												onClick={onOptimizePlan}
												disabled={isOptimizingWholePlan}
											/>
										</Grid>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
						<Grid
							container
							width={'100%'}
							justifyContent={'flex-end'}
							alignItems={'center'}
							paddingBottom={'10px'}
							paddingRight={'10px'}>
							{mainFeedbackError && mainFeedbackError.status && (
								<Typography
									marginRight={'10px'}
									style={helperText}>
									{mainFeedbackError.msg}
								</Typography>
							)}
						</Grid>
					</Grid>
					<Grid
						container
						justifyContent={'space-between'}
						paddingY={'10px'}>
						<Button
							onClick={onGeneratePdf}
							sx={{
								backgroundColor: 'transparent',
								paddingX: '15px',
								paddingY: '4px',
								color: '#28696D',
								textTransform: 'none',
								marginRight: '10px',
								fontWeight: '600',
							}}>
							Export
						</Button>
						<Grid justifyContent={'space-between'}>
							<Button
								onClick={onStoreOptimizedPlan}
								sx={{
									border: '2px solid #28696D',
									paddingX: '15px',
									paddingY: '4px',
									color: '#28696D',
									textTransform: 'none',
								}}>
								Save
							</Button>
							{/* <IconButton
								Icon={GenerateIcon}
								text={'Optimize Entire Plan'}
								onClick={onOptimizePlan}
							/> */}
						</Grid>
					</Grid>
				</>
			)}
		</>
	);
}

export default GeneratedPlanTemplate;
